import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

type SignalFieldCanvasProps = {
  mobile: boolean;
  subdued: boolean;
};

type SignalGeometry = {
  logo: THREE.BufferGeometry;
  trails: THREE.BufferGeometry;
  dust: THREE.BufferGeometry;
};

const particleVertexShader = `
  uniform float uTime;
  uniform float uPointSize;
  uniform float uMotion;
  attribute float aSeed;
  attribute float aEnergy;
  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    vec3 transformed = position;
    float entrance = smoothstep(0.0, 1.0, clamp((uTime - 0.04) / 1.18, 0.0, 1.0));
    float scatter = 1.0 - entrance;
    transformed.x += sign(position.x + 0.001) * scatter * (3.2 + aSeed * 6.8);
    transformed.y += (aSeed - 0.5) * scatter * 4.6;
    transformed.z += (aSeed - 0.5) * scatter * 10.0;
    float wave = sin(uTime * (0.34 + aSeed * 0.22) + aSeed * 31.0);
    transformed.z += wave * 0.19 * uMotion;
    transformed.x += sin(uTime * 0.22 + aSeed * 17.0) * 0.035 * uMotion;
    transformed.y += cos(uTime * 0.18 + aSeed * 13.0) * 0.018 * uMotion;
    vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = uPointSize * (16.0 / max(2.0, -mvPosition.z)) * (0.72 + aEnergy * 0.5 + wave * 0.08);
    vColor = color;
    vAlpha = (0.68 + aEnergy * 0.32) * smoothstep(0.08, 0.52, entrance);
  }
`;

const particleFragmentShader = `
  uniform float uOpacity;
  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv);
    float body = 1.0 - smoothstep(0.18, 0.5, d);
    float core = 1.0 - smoothstep(0.0, 0.13, d);
    vec3 glowColor = mix(vColor, vec3(0.08, 0.92, 0.3), core * 0.34);
    gl_FragColor = vec4(glowColor, body * vAlpha * uOpacity);
  }
`;

const dustVertexShader = `
  uniform float uTime;
  uniform float uPointSize;
  uniform float uMotion;
  attribute float aSeed;
  attribute float aEnergy;
  varying float vAlpha;

  void main() {
    vec3 transformed = position;
    float entrance = smoothstep(0.0, 1.0, clamp((uTime - 0.16) / 1.3, 0.0, 1.0));
    float scatter = 1.0 - entrance;
    transformed.x += sign(position.x + 0.001) * scatter * (2.2 + aSeed * 5.0);
    transformed.y += (aSeed - 0.5) * scatter * 3.2;
    float drift = fract(aSeed + uTime * (0.018 + aEnergy * 0.022));
    transformed.x += (drift - 0.5) * (0.8 + aEnergy * 1.7) * uMotion;
    transformed.z += sin(uTime * 0.22 + aSeed * 19.0) * 0.22 * uMotion;
    vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    gl_PointSize = uPointSize * (13.0 / max(2.0, -mvPosition.z)) * (0.55 + aEnergy * 0.8);
    vAlpha = (0.18 + aEnergy * 0.64) * smoothstep(0.08, 0.5, entrance);
  }
`;

const dustFragmentShader = `
  uniform float uOpacity;
  varying float vAlpha;

  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv);
    float alpha = 1.0 - smoothstep(0.12, 0.5, d);
    gl_FragColor = vec4(0.02, 1.0, 0.34, alpha * vAlpha * uOpacity);
  }
`;

const trailVertexShader = `
  uniform float uTime;
  uniform float uMotion;
  attribute float aPhase;
  attribute float aAlpha;
  varying float vAlpha;

  void main() {
    vec3 transformed = position;
    transformed.x += sin(uTime * 0.36 + aPhase) * 0.09 * uMotion;
    transformed.z += cos(uTime * 0.24 + aPhase) * 0.18 * uMotion;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
    float entrance = smoothstep(0.52, 1.35, uTime);
    vAlpha = aAlpha * (0.76 + sin(uTime * 0.72 + aPhase) * 0.24) * entrance;
  }
`;

const trailFragmentShader = `
  uniform float uOpacity;
  varying float vAlpha;

  void main() {
    gl_FragColor = vec4(0.0, 1.0, 0.32, vAlpha * uOpacity);
  }
`;

const flareVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const flareFragmentShader = `
  uniform float uOpacity;
  varying vec2 vUv;
  void main() {
    vec2 p = vUv - 0.5;
    float glow = exp(-(p.x * p.x * 20.0 + p.y * p.y * 90.0));
    float core = exp(-(p.x * p.x * 64.0 + p.y * p.y * 180.0));
    gl_FragColor = vec4(0.02, 1.0, 0.34, (glow * 0.32 + core * 0.58) * uOpacity);
  }
`;

const referencePlaneVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const referencePlaneFragmentShader = `
  uniform sampler2D uTexture;
  uniform float uOpacity;
  uniform float uTime;
  varying vec2 vUv;

  void main() {
    if (vUv.x < 0.296 || vUv.y > 0.91) discard;
    vec4 source = texture2D(uTexture, vUv);
    float signal = smoothstep(0.02, 0.12, source.g - max(source.r, source.b) * 1.018);
    float brightness = max(source.r, max(source.g, source.b));
    float scan = 0.965 + sin(vUv.y * 940.0 + uTime * 0.55) * 0.035;
    float alpha = signal * smoothstep(0.035, 0.18, brightness) * uOpacity;
    if (alpha < 0.01) discard;
    gl_FragColor = vec4(source.rgb * vec3(0.88, 1.08, 1.04) * scan, alpha);
  }
`;

function ReferenceSignalPlane({ texture, subdued }: { texture: THREE.Texture; subdued: boolean }) {
  const material = useRef<THREE.ShaderMaterial>(null);
  const { viewport } = useThree();

  useEffect(() => {
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
  }, [texture]);

  useFrame((state, delta) => {
    if (!material.current) return;
    material.current.uniforms.uTime.value = state.clock.elapsedTime;
    material.current.uniforms.uOpacity.value = THREE.MathUtils.damp(
      material.current.uniforms.uOpacity.value,
      subdued ? 0.06 : 0.72,
      4,
      delta
    );
  });

  return (
    <mesh position={[0, 0, 0]} renderOrder={-10}>
      <planeGeometry args={[viewport.width, viewport.height]} />
      <shaderMaterial
        ref={material}
        transparent
        depthTest={false}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        vertexShader={referencePlaneVertexShader}
        fragmentShader={referencePlaneFragmentShader}
        uniforms={{
          uTexture: { value: texture },
          uOpacity: { value: 0 },
          uTime: { value: 0 }
        }}
      />
    </mesh>
  );
}

function pseudoRandom(value: number) {
  const x = Math.sin(value * 91.3458) * 47453.5453;
  return x - Math.floor(x);
}

function makeSignalGeometry(image: HTMLImageElement, mobile: boolean): SignalGeometry {
  const size = mobile ? 300 : 440;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    return {
      logo: new THREE.BufferGeometry(),
      trails: new THREE.BufferGeometry(),
      dust: new THREE.BufferGeometry()
    };
  }

  context.drawImage(image, 0, 0, size, size);
  const pixels = context.getImageData(0, 0, size, size).data;
  const scanMaxY = Math.floor(size * 0.64);
  const stride = mobile ? 2 : 1;
  const samples: Array<{ x: number; y: number; green: number; brightness: number }> = [];
  let minX = size;
  let minY = size;
  let maxX = 0;
  let maxY = 0;

  for (let y = 2; y < scanMaxY; y += stride) {
    for (let x = 2; x < size - 2; x += stride) {
      const index = (y * size + x) * 4;
      const red = pixels[index];
      const green = pixels[index + 1];
      const blue = pixels[index + 2];
      const brightness = Math.max(red, green, blue);
      if (brightness < 74 || green < 52) continue;
      const edgeRadius = mobile ? 3 : 4;
      const brightnessAt = (sampleX: number, sampleY: number) => {
        const offset = (sampleY * size + sampleX) * 4;
        return Math.max(pixels[offset], pixels[offset + 1], pixels[offset + 2]);
      };
      const edge = Math.min(
        brightnessAt(Math.max(0, x - edgeRadius), y),
        brightnessAt(Math.min(size - 1, x + edgeRadius), y),
        brightnessAt(x, Math.max(0, y - edgeRadius)),
        brightnessAt(x, Math.min(size - 1, y + edgeRadius))
      ) < 66;
      const keepChance = edge ? 0.92 : mobile ? 0.42 : 0.28;
      if (pseudoRandom(index + 503) > keepChance) continue;
      samples.push({ x, y, green, brightness });
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  const logoPositions: number[] = [];
  const logoColors: number[] = [];
  const seeds: number[] = [];
  const energies: number[] = [];
  const trailPositions: number[] = [];
  const trailPhases: number[] = [];
  const trailAlphas: number[] = [];
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);

  samples.forEach((sample, index) => {
    const seed = pseudoRandom(index + 19);
    const nx = ((sample.x - minX) / width - 0.5) * 8.85;
    const ny = (0.5 - (sample.y - minY) / height) * 6.46;
    const depth = (pseudoRandom(index + 41) - 0.5) * (mobile ? 0.9 : 1.55);
    const jitterX = (pseudoRandom(index + 67) - 0.5) * 0.026;
    const jitterY = (pseudoRandom(index + 83) - 0.5) * 0.026;
    const energy = 0.42 + (sample.brightness / 255) * 0.58;

    logoPositions.push(nx + jitterX, ny + jitterY, depth);
    const greenBias = sample.green / Math.max(1, sample.brightness);
    logoColors.push(0.008 + greenBias * 0.028, 0.48 + energy * 0.3, 0.1 + energy * 0.12);
    seeds.push(seed);
    energies.push(energy);

    const every = mobile ? 9 : 6;
    if (index % every === 0) {
      const radialLength = Math.max(0.45, Math.hypot(nx, ny * 0.76));
      const directionX = nx / radialLength;
      const directionY = (ny * 0.42) / radialLength;
      const nearCenter = 1 - Math.min(1, Math.abs(nx) / 4.4);
      const length = (mobile ? 0.42 : 0.8) + pseudoRandom(index + 101) * (mobile ? 1.3 : 4.6) + nearCenter * (mobile ? 0.3 : 1.05);
      const yDrift = (pseudoRandom(index + 127) - 0.5) * (mobile ? 0.18 : 0.46);
      const zDrift = (pseudoRandom(index + 149) - 0.5) * (mobile ? 0.8 : 2.6);
      const phase = pseudoRandom(index + 171) * Math.PI * 2;
      const alpha = 0.18 + pseudoRandom(index + 193) * 0.6;
      trailPositions.push(nx, ny, depth, nx + directionX * length, ny + directionY * length + yDrift, depth + zDrift);
      trailPhases.push(phase, phase);
      trailAlphas.push(alpha, 0.02);
    }
  });

  const dustCount = mobile ? 900 : 3400;
  const dustPositions: number[] = [];
  const dustSeeds: number[] = [];
  const dustEnergy: number[] = [];
  for (let index = 0; index < dustCount; index += 1) {
    const seed = pseudoRandom(index + 701);
    const side = pseudoRandom(index + 719) < 0.5 ? -1 : 1;
    const radius = Math.pow(pseudoRandom(index + 733), 0.62);
    const x = side * (2.2 + radius * (mobile ? 4.8 : 9.2));
    const y = (pseudoRandom(index + 751) - 0.5) * (mobile ? 5.4 : 8.8) * (0.48 + radius * 0.52);
    const z = (pseudoRandom(index + 769) - 0.5) * (mobile ? 3.5 : 8.0);
    dustPositions.push(x, y, z);
    dustSeeds.push(seed);
    dustEnergy.push(0.18 + pseudoRandom(index + 787) * 0.82);
  }

  const logo = new THREE.BufferGeometry();
  logo.setAttribute("position", new THREE.Float32BufferAttribute(logoPositions, 3));
  logo.setAttribute("color", new THREE.Float32BufferAttribute(logoColors, 3));
  logo.setAttribute("aSeed", new THREE.Float32BufferAttribute(seeds, 1));
  logo.setAttribute("aEnergy", new THREE.Float32BufferAttribute(energies, 1));

  const trails = new THREE.BufferGeometry();
  trails.setAttribute("position", new THREE.Float32BufferAttribute(trailPositions, 3));
  trails.setAttribute("aPhase", new THREE.Float32BufferAttribute(trailPhases, 1));
  trails.setAttribute("aAlpha", new THREE.Float32BufferAttribute(trailAlphas, 1));

  const dust = new THREE.BufferGeometry();
  dust.setAttribute("position", new THREE.Float32BufferAttribute(dustPositions, 3));
  dust.setAttribute("aSeed", new THREE.Float32BufferAttribute(dustSeeds, 1));
  dust.setAttribute("aEnergy", new THREE.Float32BufferAttribute(dustEnergy, 1));

  return { logo, trails, dust };
}

function makeReferenceSignalGeometry(image: HTMLImageElement): SignalGeometry {
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    return {
      logo: new THREE.BufferGeometry(),
      trails: new THREE.BufferGeometry(),
      dust: new THREE.BufferGeometry()
    };
  }

  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  const positions: number[] = [];
  const colors: number[] = [];
  const seeds: number[] = [];
  const energies: number[] = [];
  const trailPositions: number[] = [];
  const trailPhases: number[] = [];
  const trailAlphas: number[] = [];

  for (let y = 92; y < height - 12; y += 1) {
    for (let x = 470; x < width - 2; x += 1) {
      const pixelIndex = (y * width + x) * 4;
      const red = pixels[pixelIndex];
      const green = pixels[pixelIndex + 1];
      const blue = pixels[pixelIndex + 2];
      const brightness = Math.max(red, green, blue);
      const isSignal = green > 8 && green >= red * 1.012 && green >= blue * 1.018;
      if (!isSignal) continue;
      const densityRoll = pseudoRandom(pixelIndex + 919);
      if (brightness < 30 && densityRoll > 0.13) continue;
      if (brightness >= 30 && brightness < 58 && densityRoll > 0.48) continue;

      const energy = THREE.MathUtils.clamp((brightness - 6) / 249, 0.045, 1);

      const seed = pseudoRandom(pixelIndex + 947);
      const localX = (x - 1137) / 96;
      const localY = (447 - y) / 99;
      const depth = (pseudoRandom(pixelIndex + 977) - 0.5) * (0.12 + energy * 0.38);
      positions.push(localX, localY, depth);
      colors.push(
        0.002 + energy * 0.012,
        0.04 + energy * 1.24,
        0.025 + energy * 0.22
      );
      seeds.push(seed);
      energies.push(energy);

      const isCore = x > 640 && x < 1515 && y > 100 && y < 850 && brightness > 34;
      if (isCore && pseudoRandom(pixelIndex + 1013) < 0.0065) {
        const horizontalDirection = x < 1137 ? -1 : 1;
        const length = 0.75 + Math.pow(pseudoRandom(pixelIndex + 1031), 1.55) * 6.2;
        const verticalDirection = localY * (0.035 + pseudoRandom(pixelIndex + 1051) * 0.085);
        const phase = pseudoRandom(pixelIndex + 1103) * Math.PI * 2;
        const segmentCount = 3 + Math.floor(pseudoRandom(pixelIndex + 1117) * 4);
        const dashLength = 0.045 + pseudoRandom(pixelIndex + 1129) * 0.12;
        const trailDepth = depth + (pseudoRandom(pixelIndex + 1141) - 0.5) * 0.8;
        const fullEndX = localX + horizontalDirection * length;
        const fullEndY = localY + verticalDirection * length;

        trailPositions.push(localX, localY, trailDepth, fullEndX, fullEndY, trailDepth);
        trailPhases.push(phase, phase);
        trailAlphas.push(0.025 + energy * 0.07, 0.002);

        for (let segment = 0; segment < segmentCount; segment += 1) {
          const startT = (segment + pseudoRandom(pixelIndex + segment * 17 + 1151) * 0.35) / segmentCount;
          const endT = Math.min(1, startT + dashLength / Math.max(0.75, length));
          const startX = localX + horizontalDirection * length * startT;
          const startY = localY + verticalDirection * length * startT;
          const endX = localX + horizontalDirection * length * endT;
          const endY = localY + verticalDirection * length * endT;
          const dashDepth = depth + (pseudoRandom(pixelIndex + segment * 19 + 1171) - 0.5) * 0.8;
          trailPositions.push(startX, startY, dashDepth, endX, endY, dashDepth);
          trailPhases.push(phase, phase);
          trailAlphas.push(0.12 + energy * 0.28, 0.025 + energy * 0.08);
        }
      }

    }
  }

  const logo = new THREE.BufferGeometry();
  logo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  logo.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  logo.setAttribute("aSeed", new THREE.Float32BufferAttribute(seeds, 1));
  logo.setAttribute("aEnergy", new THREE.Float32BufferAttribute(energies, 1));

  const trails = new THREE.BufferGeometry();
  trails.setAttribute("position", new THREE.Float32BufferAttribute(trailPositions, 3));
  trails.setAttribute("aPhase", new THREE.Float32BufferAttribute(trailPhases, 1));
  trails.setAttribute("aAlpha", new THREE.Float32BufferAttribute(trailAlphas, 1));
  const dust = new THREE.BufferGeometry();
  dust.setAttribute("position", new THREE.Float32BufferAttribute([], 3));
  dust.setAttribute("aSeed", new THREE.Float32BufferAttribute([], 1));
  dust.setAttribute("aEnergy", new THREE.Float32BufferAttribute([], 1));

  return { logo, trails, dust };
}

function BloomPipeline({ subdued, mobile }: SignalFieldCanvasProps) {
  const { gl, scene, camera, size } = useThree();
  const composer = useMemo(() => {
    const next = new EffectComposer(gl);
    next.addPass(new RenderPass(scene, camera));
    next.addPass(new UnrealBloomPass(new THREE.Vector2(size.width, size.height), mobile ? 0.36 : 0.27, mobile ? 0.42 : 0.24, mobile ? 0.34 : 0.46));
    return next;
  }, [camera, gl, mobile, scene, size.height, size.width]);

  useEffect(() => {
    composer.setSize(size.width, size.height);
    composer.setPixelRatio(Math.min(window.devicePixelRatio, mobile ? 1.1 : 1.3));
    return () => composer.dispose();
  }, [composer, mobile, size.height, size.width]);

  useEffect(() => {
    const bloom = composer.passes.find((pass) => pass instanceof UnrealBloomPass) as UnrealBloomPass | undefined;
    if (bloom) bloom.strength = subdued ? 0.1 : mobile ? 0.36 : 0.34;
  }, [composer, mobile, subdued]);

  useFrame(() => composer.render(), 1);
  return null;
}

function SignalScene({ mobile, subdued }: SignalFieldCanvasProps) {
  const texture = useLoader(
    THREE.TextureLoader,
    mobile ? "/assets/yeyang-logo-cropped.png" : "/assets/hero-particle-reference.png"
  );
  const group = useRef<THREE.Group>(null);
  const logoMaterial = useRef<THREE.ShaderMaterial>(null);
  const auraMaterial = useRef<THREE.ShaderMaterial>(null);
  const dustMaterial = useRef<THREE.ShaderMaterial>(null);
  const trailMaterial = useRef<THREE.ShaderMaterial>(null);
  const flareMaterial = useRef<THREE.ShaderMaterial>(null);
  const seam = useRef<THREE.Group>(null);
  const { pointer } = useThree();
  const geometry = useMemo(
    () => mobile
      ? makeSignalGeometry(texture.image as HTMLImageElement, true)
      : makeReferenceSignalGeometry(texture.image as HTMLImageElement),
    [mobile, texture.image]
  );

  useEffect(() => () => {
    geometry.logo.dispose();
    geometry.trails.dispose();
    geometry.dust.dispose();
  }, [geometry]);

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;
    const motion = subdued ? 0.18 : 1;
    const targetLogoOpacity = subdued ? 0.1 : 1;
    if (logoMaterial.current) {
      logoMaterial.current.uniforms.uTime.value = time;
      logoMaterial.current.uniforms.uMotion.value = motion;
      logoMaterial.current.uniforms.uOpacity.value = THREE.MathUtils.damp(logoMaterial.current.uniforms.uOpacity.value, targetLogoOpacity, 4.5, delta);
    }
    if (auraMaterial.current) {
      auraMaterial.current.uniforms.uTime.value = time;
      auraMaterial.current.uniforms.uMotion.value = motion;
      auraMaterial.current.uniforms.uOpacity.value = THREE.MathUtils.damp(auraMaterial.current.uniforms.uOpacity.value, subdued ? 0.015 : mobile ? 0.075 : 0.022, 4, delta);
    }
    if (dustMaterial.current) {
      dustMaterial.current.uniforms.uTime.value = time;
      dustMaterial.current.uniforms.uMotion.value = motion;
      dustMaterial.current.uniforms.uOpacity.value = THREE.MathUtils.damp(dustMaterial.current.uniforms.uOpacity.value, subdued ? 0.03 : mobile ? 0.32 : 0.34, 4, delta);
    }
    if (trailMaterial.current) {
      trailMaterial.current.uniforms.uTime.value = time;
      trailMaterial.current.uniforms.uMotion.value = motion;
      trailMaterial.current.uniforms.uOpacity.value = THREE.MathUtils.damp(trailMaterial.current.uniforms.uOpacity.value, subdued ? 0.012 : mobile ? 0.12 : 0.1, 4, delta);
    }
    if (flareMaterial.current) {
      flareMaterial.current.uniforms.uOpacity.value = THREE.MathUtils.damp(flareMaterial.current.uniforms.uOpacity.value, subdued ? 0.025 : 0.72, 3, delta);
    }
    if (group.current) {
      const targetY = (mobile ? 0 : -0.004) + pointer.x * (mobile ? 0.012 : 0.01);
      const targetX = pointer.y * (mobile ? 0.009 : 0.008);
      group.current.rotation.y = THREE.MathUtils.damp(group.current.rotation.y, targetY, 3, delta);
      group.current.rotation.x = THREE.MathUtils.damp(group.current.rotation.x, targetX, 3, delta);
      const targetScale = subdued ? (mobile ? 0.62 : 0.86) : (mobile ? 0.78 : 1);
      const nextScale = THREE.MathUtils.damp(group.current.scale.x, targetScale, 3, delta);
      group.current.scale.set(nextScale, nextScale * (mobile ? 1.05 : 1), nextScale);
    }
    if (seam.current) seam.current.scale.y = 0.985 + Math.sin(time * 1.15) * 0.015;
  });

  const commonUniforms = (pointSize: number, opacity: number) => ({
    uTime: { value: 0 },
    uPointSize: { value: pointSize },
    uMotion: { value: 1 },
    uOpacity: { value: opacity }
  });

  return (
    <>
    {!mobile ? <ReferenceSignalPlane texture={texture} subdued={subdued} /> : null}
    <group ref={group} position={mobile ? [0, 1.12, -1.2] : [3.06, -0.02, -0.65]} rotation={[0, 0, 0]}>
      <points geometry={geometry.dust}>
        <shaderMaterial
          ref={dustMaterial}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          vertexShader={dustVertexShader}
          fragmentShader={dustFragmentShader}
          uniforms={commonUniforms(mobile ? 0.92 : 0.62, subdued ? 0.03 : mobile ? 0.32 : 0.34)}
        />
      </points>
      <lineSegments geometry={geometry.trails}>
        <shaderMaterial
          ref={trailMaterial}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          vertexShader={trailVertexShader}
          fragmentShader={trailFragmentShader}
          uniforms={{ uTime: { value: 0 }, uMotion: { value: 1 }, uOpacity: { value: subdued ? 0.012 : mobile ? 0.12 : 0.1 } }}
        />
      </lineSegments>
      <points geometry={geometry.logo}>
        <shaderMaterial
          ref={auraMaterial}
          vertexColors
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          vertexShader={particleVertexShader}
          fragmentShader={particleFragmentShader}
          uniforms={commonUniforms(mobile ? 4.4 : 1.6, subdued ? 0.015 : 0.008)}
        />
      </points>
      <points geometry={geometry.logo}>
        <shaderMaterial
          ref={logoMaterial}
          vertexColors
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          vertexShader={particleVertexShader}
          fragmentShader={particleFragmentShader}
          uniforms={commonUniforms(mobile ? 1.7 : 0.82, subdued ? 0.1 : mobile ? 1 : 0.28)}
        />
      </points>

      <group ref={seam} position={[0.015, mobile ? 0 : 0.24, 1.15]}>
        <mesh>
          <planeGeometry args={[mobile ? 0.016 : 0.012, mobile ? 8.0 : 10.1]} />
          <meshBasicMaterial color="#d8ffe8" transparent opacity={subdued ? 0.13 : mobile ? 1 : 0.08} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0, -0.04]}>
          <planeGeometry args={[mobile ? 0.072 : 0.052, mobile ? 8.2 : 10.35]} />
          <meshBasicMaterial color="#19ff79" transparent opacity={subdued ? 0.03 : mobile ? 0.42 : 0.02} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0, -0.1]}>
          <planeGeometry args={[mobile ? 0.42 : 0.22, mobile ? 8.5 : 10.7]} />
          <meshBasicMaterial color="#00e66f" transparent opacity={subdued ? 0.008 : mobile ? 0.075 : 0} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
      </group>

      {mobile ? <mesh position={[0.04, -3.1, 0.3]}>
        <planeGeometry args={[mobile ? 4.6 : 7.2, mobile ? 1.35 : 1.7]} />
        <shaderMaterial
          ref={flareMaterial}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          vertexShader={flareVertexShader}
          fragmentShader={flareFragmentShader}
          uniforms={{ uOpacity: { value: subdued ? 0.025 : 0.72 } }}
        />
      </mesh> : null}

      {(mobile ? [4.35, 5.15, 6.0] : [3.82, 5.22]).map((radius, index) => (
        <mesh key={radius} position={[0.015, mobile ? 0.08 : 0.24, -1.35 - index * 0.18]} scale={[1, mobile ? 0.92 : 1, 1]}>
          <ringGeometry args={[radius, radius + (index === 0 ? 0.014 : 0.01), 192]} />
          <meshBasicMaterial color="#00f276" transparent opacity={(subdued ? 0.012 : mobile ? 0.13 : 0.085) / (index + 1)} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
      ))}
    </group>
    </>
  );
}

export function canUseWebGL() {
  try {
    const canvas = document.createElement("canvas");
    return Boolean(canvas.getContext("webgl2") || canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

export function SignalFieldCanvas(props: SignalFieldCanvasProps) {
  return (
    <Canvas
      dpr={props.mobile ? [1, 1.2] : [1, 1.45]}
      camera={{ position: [0, 0, props.mobile ? 12.2 : 12.4], fov: props.mobile ? 43 : 40, near: 0.1, far: 60 }}
      gl={{ alpha: true, antialias: true, powerPreference: "high-performance" }}
      onCreated={({ gl }) => {
        gl.toneMapping = props.mobile ? THREE.ACESFilmicToneMapping : THREE.NoToneMapping;
        gl.toneMappingExposure = props.mobile ? 0.92 : 1;
      }}
    >
      <Suspense fallback={null}>
        <SignalScene {...props} />
        <BloomPipeline {...props} />
      </Suspense>
    </Canvas>
  );
}
